import React from 'react';
import styles from './styles.module.scss';
import icThg from 'assets/img/thetan_gem_V2.png';
import icTicket from 'assets/svg/ic_ticket.svg';
import icInfo from 'assets/svg/ic_info_transparent.svg';
import icArrow from 'assets/svg/ic_arrow.svg';
import icProcessing from 'assets/gif/ic_processing.gif';
import classNames from 'classnames';
import Button from '../Button';
import TextCard from '../TextCard';
import ErrorMessage from '../ErrorMessage';
import BreakpointContent from 'src/Components/BreakpointContent';
import { CurrencyType, formatCurrency } from '@wolffun/system-currency';
import { useStaking } from '../../StakingProvider';
import { StakingPlan, StakingStatus } from 'src/models';
import { stakingPlanMap } from './config';
import Tooltip from 'src/Pages/Staking/Components/Tooltip';
import { formatDate, formatTimeLeft } from './utils';
import dayjs from 'dayjs';
import { useUserWalletContext } from '@profile/src/Profile_V2/UserWalletProvider';

const StakingItem = ({
  item,
  apr,
  config
}) => {
  const {
    unstake,
    claimReward
  } = useStaking();
  const {
    checkWalletError
  } = useUserWalletContext();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const mapData = stakingPlanMap[item.plan];
  const countDownClaimInHours = config.countdownClaim / 60 / 60;
  const isPending = item.status === StakingStatus.PENDING;
  const isUnstaked = !item.canUnstake && item.status === StakingStatus.UNSTAKE_VERIFIED;

  const renderRewardTooltip = () => /*#__PURE__*/React.createElement(Tooltip, {
    color: "#3C32AF",
    className: styles.tooltip,
    content: /*#__PURE__*/React.createElement("div", {
      className: styles.tooltipContainer
    }, /*#__PURE__*/React.createElement("ul", {
      className: styles.list
    }, /*#__PURE__*/React.createElement("li", null, `Max. claimable amount: ${formatCurrency(config.maxClaimAmount, {
      withCurrency: true,
      trimWholeFraction: true
    })} / claim`), /*#__PURE__*/React.createElement("li", null, `Each claim is ${countDownClaimInHours} hours after the previous claim`), /*#__PURE__*/React.createElement("li", null, "When claiming THG rewards, you also claim all pending Private Ticket rewards.")))
  }, /*#__PURE__*/React.createElement("img", {
    src: icInfo
  }));

  const renderDetailTooltip = () => item.stakedAt && /*#__PURE__*/React.createElement(Tooltip, {
    className: styles.tooltip,
    color: "#3C32AF",
    content: /*#__PURE__*/React.createElement("div", {
      className: styles.tooltipContainer
    }, /*#__PURE__*/React.createElement("ul", {
      className: styles.list
    }, /*#__PURE__*/React.createElement("li", null, `Your staking date: ${dayjs(item.stakedAt).format('DD MMM[,] YYYY')}`), /*#__PURE__*/React.createElement("li", null, `Time left: ${formatTimeLeft(item.unstakedAt)}`)))
  }, /*#__PURE__*/React.createElement("img", {
    className: styles.img,
    src: icInfo
  }));

  const renderCollapsible = (withClick = false) => /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.collapsible, !isUnstaked && !isPending && styles.claimable)
  }, /*#__PURE__*/React.createElement("img", {
    draggable: false,
    onClick: withClick ? () => setIsCollapsed(e => !e) : undefined,
    className: classNames(styles.collapseIcon, isCollapsed && !isPending ? styles.collapsed : null),
    src: icArrow
  }));

  const renderBalanceDesktop = () => /*#__PURE__*/React.createElement("div", {
    className: styles.balanceContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Staked Amount"), /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, item.status === StakingStatus.UNSTAKE_VERIFIED ? 'Unstaked Date' : item.plan !== StakingPlan.FLEXIBLE ? 'Due Date' : '')), /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, formatCurrency(item.amount, {
    withCurrency: true,
    trimWholeFraction: true
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.textIcon
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, item.status === StakingStatus.UNSTAKE_VERIFIED || item.plan !== StakingPlan.FLEXIBLE ? formatDate(item.unstakedAt) : ''), item.status !== StakingStatus.UNSTAKE_VERIFIED && item.plan !== StakingPlan.FLEXIBLE && renderDetailTooltip())));

  const renderBalanceMobile = () => /*#__PURE__*/React.createElement("div", {
    className: styles.balanceContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Staked Amount"), /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, formatCurrency(item.amount, {
    withCurrency: true,
    trimWholeFraction: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, item.status === StakingStatus.UNSTAKE_VERIFIED ? 'Unstaked Date' : item.plan !== StakingPlan.FLEXIBLE ? 'Due Date' : ''), /*#__PURE__*/React.createElement("div", {
    className: styles.textIcon
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, item.status === StakingStatus.UNSTAKE_VERIFIED || item.plan !== StakingPlan.FLEXIBLE ? formatDate(item.unstakedAt) : ''), item.status !== StakingStatus.UNSTAKE_VERIFIED && item.plan !== StakingPlan.FLEXIBLE && renderDetailTooltip())));

  const renderStakingPlan = () => {
    switch (item.plan) {
      case StakingPlan.ONE_YEAR:
        return /*#__PURE__*/React.createElement("div", {
          className: classNames(styles.border, styles.plan)
        }, /*#__PURE__*/React.createElement("span", {
          className: styles.title
        }, "High Rewards Stake"), /*#__PURE__*/React.createElement("span", {
          className: styles.text
        }, "12 Months"));

      case StakingPlan.SIX_MONTHS:
        return /*#__PURE__*/React.createElement("div", {
          className: classNames(styles.border, styles.plan)
        }, /*#__PURE__*/React.createElement("span", {
          className: styles.title
        }, "High Rewards Stake"), /*#__PURE__*/React.createElement("span", {
          className: styles.text
        }, "6 Months"));

      case StakingPlan.FLEXIBLE:
        return /*#__PURE__*/React.createElement("div", {
          className: classNames(styles.border, styles.plan)
        }, /*#__PURE__*/React.createElement("span", {
          className: styles.title
        }, "Flexible Stake"));

      default:
        return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
  };

  const renderStakeErrorMessage = () => {
    if (!item.canUnstake && item.status === StakingStatus.UNSTAKE_VERIFIED) {
      return /*#__PURE__*/React.createElement(ErrorMessage, {
        className: styles.error,
        message: "You are previous unstake"
      });
    }

    const dueDate = item.stakedAt !== undefined ? item.stakedAt + config.plans[item.plan].lockDuration * 1000 : undefined;

    if (dueDate !== undefined && !item.canUnstake && item.plan !== StakingPlan.FLEXIBLE && dueDate - Date.now() > 0) {
      return /*#__PURE__*/React.createElement(ErrorMessage, {
        className: styles.error,
        message: "You cannot unstake yet as the token is being locked"
      });
    }

    return undefined;
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement("div", {
    onClick: () => setIsCollapsed(e => !e),
    className: classNames(styles.wrapper, isCollapsed && !isPending && styles.borderLess, !isUnstaked && !isPending && styles.claimable)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.icon
  }, isPending ? /*#__PURE__*/React.createElement("img", {
    title: "This item is processing",
    className: styles.fixed,
    src: icProcessing
  }) : /*#__PURE__*/React.createElement("img", {
    className: styles[mapData.icStyle],
    src: mapData.icon
  })), renderStakingPlan(), /*#__PURE__*/React.createElement("div", {
    className: styles.apr
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "APR"), /*#__PURE__*/React.createElement("span", {
    className: styles.textHighLight
  }, `${apr[item.plan]}%`)), /*#__PURE__*/React.createElement(BreakpointContent, {
    renderDesktop: renderBalanceDesktop,
    renderTablet: renderBalanceDesktop,
    renderPhone: renderBalanceMobile
  }), /*#__PURE__*/React.createElement(BreakpointContent, {
    renderDesktop: () => renderCollapsible(false),
    renderPhone: () => null,
    renderTablet: () => null
  })), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.detail, !isCollapsed || isPending ? styles.hidden : null)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.leftSection
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.claimSection
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.priceWrapper
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Pending Rewards:\xA0"), renderRewardTooltip()), /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, formatCurrency(item.pendingReward, {
    withCurrency: true,
    trimWholeFraction: true
  }))), item.plan !== StakingPlan.FLEXIBLE && /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Pending Private Ticket:\xA0"), /*#__PURE__*/React.createElement("div", {
    className: styles.priceWrapper
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, formatCurrency(item.pendingPrivateTickets, {
    withCurrency: false,
    trimWholeFraction: true
  })), /*#__PURE__*/React.createElement("img", {
    src: icTicket
  }))), /*#__PURE__*/React.createElement(TextCard, {
    title: "Claimable:",
    content: formatCurrency(item.claimableReward, {
      withCurrency: true,
      trimWholeFraction: true
    }),
    className: styles.input
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: () => {
      if (!checkWalletError()) {
        claimReward({
          amount: item.claimableReward,
          stakingId: item.id
        });
      }
    },
    disabled: !item.canClaimReward,
    className: styles.button,
    title: "Claim"
  }), !item.canClaimReward && /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.error,
    message: `You only claim reward each every ${countDownClaimInHours} hours`
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.claimSection
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.header, item.plan !== StakingPlan.FLEXIBLE && styles.balance)
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Staked Amount:\xA0"), /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, formatCurrency(item.amount, {
    withCurrency: true,
    trimWholeFraction: true
  }))), /*#__PURE__*/React.createElement(TextCard, {
    title: "Amount:",
    content: formatCurrency(item.status !== StakingStatus.UNSTAKE_VERIFIED ? item.amount : {
      token: CurrencyType.THG,
      value: '0'
    }, {
      trimWholeFraction: true,
      withCurrency: true
    }),
    className: styles.input
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: () => {
      if (!checkWalletError()) {
        unstake({
          stakingId: item.id,
          amount: item.amount,
          blockchainId: item.blockchainId
        });
      }
    },
    className: styles.button,
    type: "secondary",
    title: "Unstake",
    disabled: !item.canUnstake
  }), renderStakeErrorMessage())), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.rightSection, item.plan === StakingPlan.FLEXIBLE ? styles.center : undefined)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.infoContainer
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Total Claimed THG Reward"), /*#__PURE__*/React.createElement("div", {
    className: styles.textIcon
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, formatCurrency(item.totalReward, {
    withCurrency: true,
    trimWholeFraction: true
  })), /*#__PURE__*/React.createElement("img", {
    src: icThg
  }))), item.plan !== StakingPlan.FLEXIBLE && /*#__PURE__*/React.createElement("div", {
    className: styles.infoWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.infoContainer
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Total Claimed Private Ticket"), /*#__PURE__*/React.createElement("div", {
    className: styles.textIcon
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.text
  }, formatCurrency(item.privateTickets, {
    trimWholeFraction: true
  })), "\xA0", /*#__PURE__*/React.createElement("img", {
    src: icTicket
  })))))), /*#__PURE__*/React.createElement(BreakpointContent, {
    renderDesktop: () => null,
    renderPhone: () => renderCollapsible(true),
    renderTablet: () => renderCollapsible(true)
  }));
};

export default StakingItem;